import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import GoogleReviews from "@/components/GoogleReviews";
import WhatsAppButton from "@/components/WhatsAppButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MapPin, Phone, Mail, Clock } from "@/components/Icon";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { trackEvent } from "@/lib/analytics";

const contactInfo = [
  { icon: MapPin, label: "Adres", value: "Platinaweg 25, Den Haag" },
  { icon: Phone, label: "Telefoon", value: "070 887 3306", href: "tel:+31708873306" },
  { icon: Mail, label: "E-mail", value: "info@magicprint.nl", href: "mailto:info@magicprint.nl" },
  { icon: Clock, label: "Openingstijden", value: "Ma-Vr: 09:00 – 17:00" },
];

const Contact = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const fd = new FormData(form);

    const payload = {
      name: (fd.get("naam") as string) || "",
      email: (fd.get("email") as string) || "",
      subject: (fd.get("onderwerp") as string) || null,
      message: (fd.get("bericht") as string) || "",
    };

    const { supabase } = await import("@/integrations/supabase/client");
    const { error } = await supabase.from("contact_messages").insert(payload);

    if (error) {
      console.error("contact insert failed", error);
      setLoading(false);
      toast({
        title: "Er ging iets mis",
        description: "Probeer het later opnieuw of bel ons op 070 887 3306.",
        variant: "destructive",
      });
      return;
    }

    try {
      await supabase.functions.invoke("send-form-notification", {
        body: {
          type: "contact",
          data: {
            name: payload.name,
            email: payload.email,
            onderwerp: payload.subject,
            bericht: payload.message,
          },
        },
      });
    } catch (err) {
      console.error("notification mail failed", err);
    }

    setLoading(false);
    trackEvent("contact_verzonden", { onderwerp: payload.subject });
    toast({
      title: "Bericht verstuurd!",
      description: "Wij reageren zo snel mogelijk op uw bericht.",
    });
    form.reset();
  };

  return (
    <>
      <Helmet>
        <title>Contact — Magic Print Den Haag</title>
        <meta
          name="description"
          content="Neem contact op met Magic Print in Den Haag. Bel 070 887 3306, mail info@magicprint.nl of stuur een bericht — reactie binnen 1 werkdag."
        />
        <link rel="canonical" href="https://www.magicprint.nl/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Magic Print" />
        <meta property="og:locale" content="nl_NL" />
        <meta property="og:title" content="Contact — Magic Print Den Haag" />
        <meta
          property="og:description"
          content="Neem contact op met Magic Print in Den Haag. Bel, mail of stuur een bericht — reactie binnen 1 werkdag."
        />
        <meta property="og:url" content="https://www.magicprint.nl/contact" />
        <meta property="og:image" content="https://www.magicprint.nl/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact — Magic Print Den Haag" />
        <meta
          name="twitter:description"
          content="Neem contact op met Magic Print in Den Haag. Bel, mail of stuur een bericht — reactie binnen 1 werkdag."
        />
        <meta name="twitter:image" content="https://www.magicprint.nl/og-image.jpg" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Contact — Magic Print",
            url: "https://www.magicprint.nl/contact",
            about: { "@id": "https://www.magicprint.nl/#organization" },
          })}
        </script>
      </Helmet>
      <Navbar />
      <main className="pt-32 md:pt-40 pb-20 min-h-screen bg-background">
        <div className="container">
          <h1 className="text-3xl md:text-5xl font-bold mb-4">
            Neem contact op
          </h1>
          <p className="text-muted-foreground mb-12 text-lg max-w-2xl">
            Heeft u vragen over onze diensten of wilt u langskomen? Neem gerust contact met ons op.
          </p>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="grid sm:grid-cols-2 gap-6">
                {contactInfo.map((item) => (
                  <div key={item.label} className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border">
                    <item.icon className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="text-foreground font-medium hover:text-primary transition-colors">
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-foreground font-medium">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="rounded-xl overflow-hidden border border-border h-64 lg:h-auto lg:flex-1">
                <iframe
                  title="Magic Print locatie"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2462.5!2d4.3!3d52.05!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sPlatinaweg+25+Den+Haag!5e0!3m2!1snl!2snl!4v1"
                  className="w-full h-full min-h-[250px]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 bg-card p-6 md:p-8 rounded-2xl border border-border">
              <h2 className="text-xl font-bold mb-2 text-[#0a2933]">Stuur een bericht</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="naam">Naam *</Label>
                  <Input id="naam" name="naam" required placeholder="Uw naam" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail *</Label>
                  <Input id="email" name="email" type="email" required placeholder="uw@email.nl" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="onderwerp">Onderwerp</Label>
                <Input id="onderwerp" name="onderwerp" placeholder="Waar gaat het over?" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bericht">Bericht *</Label>
                <Textarea id="bericht" name="bericht" required rows={5} placeholder="Uw bericht..." />
              </div>
              <Button type="submit" size="lg" disabled={loading}>
                {loading ? "Verzenden..." : "Verstuur bericht"}
              </Button>
            </form>
          </div>
        </div>
      </main>
      <GoogleReviews />
      <Footer />
      <WhatsAppButton />
    </>
  );
};

export default Contact;
